import { Controller } from "@hotwired/stimulus"
import {
  detectMobilePlatform,
  shouldUseNativeIosSmartAppBanner
} from "../lib/mobile_app_promo_detection.js"

const STORAGE_PREFIX = "newsFeedReader.mobileAppPromo.dismissed"

export default class extends Controller {
  static targets = ["title", "message", "link"]
  static values = {
    androidMessage: String,
    androidTitle: String,
    androidUrl: String,
    dismissDays: { type: Number, default: 14 },
    handoffUrl: String,
    iosMessage: String,
    iosTitle: String,
    iosUrl: String
  }

  connect() {
    this.platform = this.detectPlatform()
    if (!this.platform || this.isStandaloneWebApp()) return

    const url = this.storeUrlFor(this.platform)
    if (!url || this.isDismissed(this.platform, url)) return

    if (shouldUseNativeIosSmartAppBanner({
      platform: this.platform,
      hasNativeSmartAppBanner: this.hasNativeSmartAppBanner(),
      userAgent: navigator.userAgent || ""
    })) return

    this.renderPrompt(this.platform, url)
  }

  dismiss(event) {
    event.preventDefault()

    const url = this.storeUrlFor(this.platform)
    if (this.platform && url) this.storeDismissal(this.platform, url)

    this.element.hidden = true
  }

  renderPrompt(platform, url) {
    if (this.hasTitleTarget) this.titleTarget.textContent = this.titleFor(platform)
    if (this.hasMessageTarget) this.messageTarget.textContent = this.messageFor(platform)

    if (this.hasLinkTarget) {
      this.linkTarget.href = url
      this.linkTarget.textContent = platform === "ios" ? "App Store" : "Play Store"
      this.linkTarget.rel = "noopener"
    }

    this.element.hidden = false
  }

  detectPlatform() {
    return detectMobilePlatform({
      userAgent: navigator.userAgent || "",
      platform: navigator.platform || "",
      maxTouchPoints: navigator.maxTouchPoints || 0
    })
  }

  storeUrlFor(platform) {
    if (this.hasHandoffUrlValue && this.handoffUrlValue.trim()) return this.handoffUrlValue.trim()
    if (platform === "ios" && this.hasIosUrlValue) return this.iosUrlValue.trim()
    if (platform === "android" && this.hasAndroidUrlValue) return this.androidUrlValue.trim()

    return ""
  }

  titleFor(platform) {
    if (platform === "ios" && this.hasIosTitleValue) return this.iosTitleValue
    if (platform === "android" && this.hasAndroidTitleValue) return this.androidTitleValue

    return "Use NewsFeedReader on mobile"
  }

  messageFor(platform) {
    if (platform === "ios" && this.hasIosMessageValue) return this.iosMessageValue
    if (platform === "android" && this.hasAndroidMessageValue) return this.androidMessageValue

    return "Get the app for a cleaner reading experience, bookmarks, and your sources on the go."
  }

  hasNativeSmartAppBanner() {
    return Boolean(document.querySelector('meta[name="apple-itunes-app"]'))
  }

  isStandaloneWebApp() {
    return window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches === true
  }

  isDismissed(platform, url) {
    const key = this.storageKey(platform, url)
    const expiresAt = Number(this.readStorage(key))

    if (!Number.isFinite(expiresAt)) return false
    if (Date.now() < expiresAt) return true

    this.removeStorage(key)
    return false
  }

  storeDismissal(platform, url) {
    const dismissDays = Math.max(1, this.dismissDaysValue || 14)
    const expiresAt = Date.now() + dismissDays * 24 * 60 * 60 * 1000

    this.writeStorage(this.storageKey(platform, url), String(expiresAt))
  }

  storageKey(platform, url) {
    return `${STORAGE_PREFIX}.${platform}.${encodeURIComponent(url)}`
  }

  readStorage(key) {
    try {
      return window.localStorage?.getItem(key)
    } catch (_error) {
      return null
    }
  }

  writeStorage(key, value) {
    try {
      window.localStorage?.setItem(key, value)
    } catch (_error) {
      return null
    }
  }

  removeStorage(key) {
    try {
      window.localStorage?.removeItem(key)
    } catch (_error) {
      return null
    }
  }
}
